
import { supabase } from '@/integrations/supabase/client';
import { SessionLockManager } from './session-lock-manager';
import { SessionValidator } from './session-validator';
import { DataStateVerifier } from './data-state-verifier';
import { CrossTabNotifier } from './cross-tab-notifier';

export interface DataDeletionProgress {
  step: string;
  completed: boolean;
  error?: string;
  skipped?: boolean;
}

export class EnhancedSupabaseDataManager {
  private lockManager: SessionLockManager;
  private sessionValidator: SessionValidator;
  private notifier: CrossTabNotifier;
  private static instance: EnhancedSupabaseDataManager;

  constructor() {
    this.lockManager = new SessionLockManager();
    this.sessionValidator = SessionValidator.getInstance();
    this.notifier = new CrossTabNotifier();
  }

  static getInstance(): EnhancedSupabaseDataManager {
    if (!EnhancedSupabaseDataManager.instance) {
      EnhancedSupabaseDataManager.instance = new EnhancedSupabaseDataManager();
    }
    return EnhancedSupabaseDataManager.instance;
  }

  async clearAllUserDataFromSupabase(profileId?: string): Promise<DataDeletionProgress[]> {
    const OPERATION = 'clear_all_data';
    
    console.log(`=== INICIANDO LIMPEZA MELHORADA DE DADOS SUPABASE ${profileId ? `(Perfil: ${profileId})` : '(Todos os dados)'} ===`);

    // Tentar adquirir lock
    const lockAcquired = await this.lockManager.acquireLock(OPERATION);
    if (!lockAcquired) {
      throw new Error('Operação já está sendo executada em outra aba. Aguarde a conclusão.');
    }

    const operations: DataDeletionProgress[] = [
      { step: 'Transações', completed: false },
      { step: 'Contas', completed: false },
      { step: 'Cartões', completed: false },
      { step: 'Categorias', completed: false },
      { step: 'Orçamentos', completed: false },
      { step: 'Metas', completed: false },
      { step: 'Dívidas', completed: false },
      { step: 'Lançamentos Futuros', completed: false },
      { step: 'Faturas de Cartão', completed: false },
      { step: 'Grupos de Parcelas', completed: false }
    ];

    this.notifier.notify('operation_started', OPERATION, { operations });

    try {
      // Validar sessão
      const isValidSession = await this.sessionValidator.isValidSession();
      if (!isValidSession) {
        const refreshed = await this.sessionValidator.refreshSessionIfNeeded();
        if (!refreshed) {
          throw new Error('Sessão inválida. Faça login novamente.');
        }
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      // Verificar estado atual dos dados
      console.log('Verificando estado atual dos dados...');
      const dataChecks = await DataStateVerifier.verifyUserDataExists();
      console.log('Estado dos dados:', dataChecks);

      const tableOperations = [
        { table: 'transactions', operation: operations[0] },
        { table: 'accounts', operation: operations[1] },
        { table: 'cards', operation: operations[2] },
        { table: 'categories', operation: operations[3] },
        { table: 'budgets', operation: operations[4] },
        { table: 'goals', operation: operations[5] },
        { table: 'debts', operation: operations[6] },
        { table: 'future_releases', operation: operations[7] },
        { table: 'credit_card_bills', operation: operations[8] },
        { table: 'installment_groups', operation: operations[9] }
      ];

      for (const { table, operation } of tableOperations) {
        try {
          // Verificar se deve pular esta operação
          const shouldSkip = await DataStateVerifier.shouldSkipDeletion(table);
          
          if (shouldSkip) {
            console.log(`Pulando exclusão da tabela ${table} - não há dados`);
            operation.completed = true;
            operation.skipped = true;
            continue;
          }

          console.log(`Deletando dados da tabela ${table}...`);
          
          let query = (supabase as any).from(table).delete().eq('user_id', user.id);
          
          // Filtrar por profile_id se fornecido
          if (profileId) {
            query = query.eq('profile_id', profileId);
          }
          
          // Tratamento especial para categorias (apenas não-padrão)
          if (table === 'categories') {
            query = query.eq('is_default', false);
          }

          const { error } = await query;

          if (error) {
            // Distinguir entre erro real e "não encontrado"
            if (error.code === 'PGRST116' || error.message.includes('does not exist')) {
              console.log(`Tabela ${table} não existe - pulando`);
              operation.completed = true;
              operation.skipped = true;
            } else if (error.code === '23503') {
              // Erro de constraint - tentar novamente após delay
              console.warn(`Constraint error na tabela ${table}, tentando novamente...`);
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              const { error: retryError } = await query;
              if (retryError) {
                throw new Error(`Erro ao deletar ${table}: ${retryError.message}`);
              }
              operation.completed = true;
            } else {
              throw new Error(`Erro ao deletar ${table}: ${error.message}`);
            }
          } else {
            operation.completed = true;
          }

        } catch (error) {
          console.error(`Erro na operação ${table}:`, error);
          operation.error = error instanceof Error ? error.message : 'Erro desconhecido';
          throw error;
        }
      }

      this.notifier.notify('data_cleared', OPERATION);
      console.log('✅ LIMPEZA MELHORADA SUPABASE FINALIZADA COM SUCESSO');
      
      return operations;

    } catch (error) {
      console.error('❌ ERRO DURANTE LIMPEZA MELHORADA SUPABASE:', error);
      
      const failedIndex = operations.findIndex(op => !op.completed && !op.skipped);
      if (failedIndex >= 0) {
        operations[failedIndex].error = error instanceof Error ? error.message : 'Erro desconhecido';
      }

      this.notifier.notify('error_occurred', OPERATION, { error: error instanceof Error ? error.message : 'Erro desconhecido' });
      throw error;
    } finally {
      this.lockManager.releaseLock(OPERATION);
      this.notifier.notify('operation_completed', OPERATION);
    }
  }

  async deleteUserAccountFromSupabase(): Promise<void> {
    const OPERATION = 'delete_account';
    
    console.log('=== INICIANDO EXCLUSÃO MELHORADA DA CONTA ===');

    const lockAcquired = await this.lockManager.acquireLock(OPERATION);
    if (!lockAcquired) {
      throw new Error('Operação de exclusão de conta já está sendo executada em outra aba.');
    }

    this.notifier.notify('operation_started', OPERATION);

    try {
      // Primeiro limpar todos os dados do usuário
      await this.clearAllUserDataFromSupabase();

      // Validar sessão novamente
      const isValidSession = await this.sessionValidator.isValidSession();
      if (!isValidSession) {
        throw new Error('Sessão inválida durante exclusão de conta');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      // Deletar perfil do usuário
      console.log('Deletando perfil do usuário...');
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);
      
      if (profileError && profileError.code !== 'PGRST116') {
        console.warn('Erro ao deletar perfil:', profileError.message);
      }

      this.notifier.notify('data_cleared', OPERATION);
      console.log('✅ EXCLUSÃO MELHORADA DA CONTA FINALIZADA');

    } catch (error) {
      console.error('❌ ERRO DURANTE EXCLUSÃO MELHORADA DA CONTA:', error);
      this.notifier.notify('error_occurred', OPERATION, { error: error instanceof Error ? error.message : 'Erro desconhecido' });
      throw error;
    } finally {
      this.lockManager.releaseLock(OPERATION);
      this.notifier.notify('operation_completed', OPERATION);
    }
  }

  async isUserAuthenticated(): Promise<boolean> {
    return await this.sessionValidator.isValidSession();
  }

  isOperationLocked(operation: string): boolean {
    return this.lockManager.isLocked(operation);
  }

  onCrossTabNotification(type: 'data_cleared' | 'operation_started' | 'operation_completed' | 'error_occurred', callback: (notification: any) => void): void {
    this.notifier.onNotification(type, callback);
  }

  removeCrossTabListener(type: 'data_cleared' | 'operation_started' | 'operation_completed' | 'error_occurred', callback: (notification: any) => void): void {
    this.notifier.removeListener(type, callback);
  }
}

// Manter compatibilidade com API antiga
export const clearAllUserDataFromSupabase = async (profileId?: string): Promise<DataDeletionProgress[]> => {
  const manager = EnhancedSupabaseDataManager.getInstance();
  return await manager.clearAllUserDataFromSupabase(profileId);
};

export const deleteUserAccountFromSupabase = async (): Promise<void> => {
  const manager = EnhancedSupabaseDataManager.getInstance();
  return await manager.deleteUserAccountFromSupabase();
};

export const isUserAuthenticated = async (): Promise<boolean> => {
  const manager = EnhancedSupabaseDataManager.getInstance();
  return await manager.isUserAuthenticated();
};
